import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Ban, Clock, FilePen, FileCheck, Lock } from "lucide-react";
import { CreateCompanyInviteDialog } from "@/components/invitations/CreateCompanyInviteDialog";

interface PortfolioStats {
  activeCompanies: number;
  disabledCompanies: number;
  pendingInvites: number;
  assessments: { draft: number; complete: number; finalized: number };
}

async function fetchPortfolioStats(): Promise<PortfolioStats> {
  const res = await fetch("/api/portfolio/stats", { credentials: "include" });
  if (!res.ok) throw new Error("Failed to fetch stats");
  return res.json();
}

// Same tile as SalesDashboard's StatCard (not exported there).
function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Building2;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 flex items-center gap-4 shadow-sm">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

export default function PortfolioOverview() {
  const queryClient = useQueryClient();
  // Query key matches what CreateCompanyInviteDialog invalidates (`${apiBase}/stats`).
  const { data, isLoading, isError } = useQuery<PortfolioStats>({
    queryKey: ["/api/portfolio/stats"],
    queryFn: fetchPortfolioStats,
  });

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Your portfolio companies and their assessments</p>
        </div>
        <CreateCompanyInviteDialog
          apiBase="/api/portfolio"
          triggerLabel="Invite a company"
          // The dialog refreshes stats/invitations; the Companies list also shows pending invites.
          onCreated={() => queryClient.invalidateQueries({ queryKey: ["/api/portfolio/companies"] })}
        />
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : isError || !data ? (
        <div className="flex h-64 items-center justify-center text-sm text-slate-500">
          Failed to load stats. Please refresh.
        </div>
      ) : (
        <>
          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Companies</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard icon={Building2} label="Active companies" value={data.activeCompanies} color="bg-emerald-500" />
              <StatCard icon={Ban} label="Disabled companies" value={data.disabledCompanies} color="bg-slate-400" />
              <StatCard icon={Clock} label="Pending invites" value={data.pendingInvites} color="bg-amber-400" />
            </div>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Assessments</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard icon={FilePen} label="Draft" value={data.assessments.draft} color="bg-slate-400" />
              <StatCard icon={FileCheck} label="Complete" value={data.assessments.complete} color="bg-blue-500" />
              <StatCard icon={Lock} label="Finalised" value={data.assessments.finalized} color="bg-primary" />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
